import {useEffect, useRef} from 'react';
import {authService} from '@/services/authService';

interface Options {
    // Refresh this many ms before session token expiry
    skewMs?: number;
    // Minimum wait between proactive refresh checks
    minIntervalMs?: number;
    // Max retry attempts for proactive refresh
    maxAttempts?: number;
}

function parseJwtExpMs(token: string | null): number | null {
    if (!token) return null;
    try {
        const parts = token.split('.');
        if (parts.length < 2 || !parts[1]) return null;
        const payload = JSON.parse(atob(parts[1]));
        if (!payload?.exp) return null;
        return Number(payload.exp) * 1000;
    } catch {
        return null;
    }
}

export function useProactiveSessionRefresh(options: Options = {}) {
    const {skewMs = 60_000, minIntervalMs = 60_000, maxAttempts = 3} = options;
    const timerRef = useRef<number | null>(null);
    const refreshingRef = useRef<boolean>(false);

    useEffect(() => {
        const schedule = () => {
            clearScheduled();

            const token = localStorage.getItem('auth_token');
            const expMs = parseJwtExpMs(token);
            const now = Date.now();

            if (!expMs) {
                // No session token or unparsable; do not schedule. Interceptor handles first 401.
                return;
            }

            // Time when we want to refresh proactively
            const target = Math.max(now + minIntervalMs, expMs - skewMs);
            const delay = Math.max(0, target - now);

            timerRef.current = window.setTimeout(async () => {
                await refreshWithRetry();
                // Reschedule after refresh attempt (success will set new token/exp)
                schedule();
            }, delay) as unknown as number;
        };

        const refreshWithRetry = async () => {
            if (refreshingRef.current) return;
            refreshingRef.current = true;
            try {
                let attempt = 0;
                while (attempt < maxAttempts) {
                    attempt += 1;
                    try {
                        await authService.refreshToken();
                        break; // success
                    } catch (err: any) {
                        // Stop retrying for auth errors where refresh is invalid
                        const status = err?.response?.status;
                        if (status === 400 || status === 401 || status === 403) {
                            break;
                        }
                        // small backoff
                        await new Promise((res) => setTimeout(res, 250 * attempt));
                    }
                }
            } finally {
                refreshingRef.current = false;
            }
        };

        const clearScheduled = () => {
            if (timerRef.current !== null) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };

        // Initial schedule
        schedule();

        // Reschedule when tokens change (cross-tab or same tab updates)
        const onStorage = (e: StorageEvent) => {
            if (e.key === 'auth_token' || e.key === 'refresh_token') {
                schedule();
            }
        };
        window.addEventListener('storage', onStorage);

        // If tab becomes visible and token is near expiry, refresh immediately
        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                const token = localStorage.getItem('auth_token');
                const expMs = parseJwtExpMs(token);
                if (expMs && expMs - Date.now() <= skewMs) {
                    // Fire-and-forget; scheduler will re-run on token change
                    void authService.refreshToken().catch(() => {
                    });
                }
            }
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            clearScheduled();
            window.removeEventListener('storage', onStorage);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [skewMs, minIntervalMs, maxAttempts]);
}
